import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { QUERY_KEYS } from '@/constants';
import { useTagLookup } from '@/hooks/useTags';
import { addedTagSetMessage } from '@/utils/addedTagSetMessage';

/**
 * Reporter for documents that got an extra tag group because they were linked
 * to a question whose tags none of their groups covered.
 *
 * Call the returned function with the `addedTagSet` of every link response that
 * carried one. It toasts what was added (see `addedTagSetMessage`) and refetches
 * the documents so the pärm shows the new shelf. It does nothing for an empty
 * list, so callers can pass whatever they collected without checking.
 */
export function useAddedTagSetToast(): (addedTagSets: string[][]) => void {
  const { label } = useTagLookup();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useCallback(
    (addedTagSets: string[][]): void => {
      const message = addedTagSetMessage(addedTagSets, label);
      if (!message) return;
      enqueueSnackbar(message, { variant: 'info', autoHideDuration: 6000 });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DOCUMENTS] });
    },
    [label, enqueueSnackbar, queryClient],
  );
}
